
"use client";
import { useState, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "./ui/button";
import { CopyIcon, ShareIcon, DownloadIcon, EyeIcon, UndoIcon, RedoIcon } from "./icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { artifactDefinitions } from "./artifact";
import { cn, handleDownload, handleView, handleDownloadCardAsImage, downloadCardAsImage, downloadPDF, } from "@/lib/utils";
import { Zap, PrinterIcon } from "lucide-react";
import { toast } from "sonner";
import { setActiveNavItem, setIsCodeView } from "@/store/mainSlice";
import { SidebarUserNav } from "@/components/sidebar-user-nav";
import { isActiveDocument } from "@/lib/dropDownData";

export function PureArtifactActions({
  artifact,
  handleVersionChange,
  currentVersionIndex,
  isCurrentVersion,
  mode,
  metadata,
  setMetadata,
  setShowBuyCreditsModal,
  // isFreeLimit,
  setShareOpen,
  htmlContent,
  // userDetail,
  // remainingWords,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [platform, setPlatform] = useState("card");
  const dispatch = useDispatch();
  const { liveWebsiteUrl, user_ai_limit, isFreeLimit, activeTab, userIdParent, isCodeView } = useSelector((state) => state.main);
  const artifactDefinition = artifactDefinitions.find(
    (definition) => definition.kind === artifact.kind
  );
  if (!artifactDefinition) {
    throw new Error("Artifact definition not found!");
  }
  const cardSizes = {
    actual: null,
    facebook: { width: 1200, height: 630 },
    instagram: { width: 1080, height: 1080 },
    twitter: { width: 1200, height: 675 },
  };

  const handlePrint = async () => {
    if (isFreeLimit) {
      setShowBuyCreditsModal(true);
      return;
    }

    try {
      // Determine what to print
      const contentToPrint =
        artifact.kind === "html"
          ? htmlContent || artifact.content
          : liveWebsiteUrl;

      // Create a new window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
      <html>
        <head>
          <title>Print Card</title>
          <style>
            body { margin: 0; padding: 0; display: flex; justify-content: center; align-items: center; }
            img, iframe { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          ${artifact.kind === "html"
          ? contentToPrint
          : `<iframe src="${contentToPrint}" width="100%" height="100%" frameborder="0"></iframe>`}
          <script>
            window.onload = () => {
              window.print();
              setTimeout(() => window.close(), 1000);
            };
          </script>
        </body>
      </html>
    `);
      printWindow.document.close();
    } catch (error) {
      console.error("Error while printing:", error);
    }
  };

  const actionContext = {
    content: artifact.content,
    handleVersionChange,
    currentVersionIndex,
    isCurrentVersion,
    mode,
    metadata,
    setMetadata,
  };
  const extraActions = [
    {
      description: "Download file",
      icon: <DownloadIcon size={24} className="ml-5" />,
      onClick: async () => {
        if (isFreeLimit) {
          setShowBuyCreditsModal(true);
          return;
        }
        // 🧭 Determine which tab is active
        if (activeTab === "Card Maker") {
          const fileName = `card.png`;
          const contentToUse =
            artifact.kind === "html"
              ? htmlContent || artifact.content
              : liveWebsiteUrl;

          // console.log("🟣 Download Card triggered", contentToUse);
          await handleDownloadCardAsImage(contentToUse, fileName);
        } else if (activeTab === "Page Builder") {
          const fileName = `artifact-chatbot.html`;
          // console.log("🟢 Download Page triggered");
          await handleDownload(liveWebsiteUrl, fileName);
        } else {
          await downloadPDF(artifact.content)
        }
      },
    },
    ...(activeTab === "Card Maker"
      ? [
        {
          description: "Print Card",
          icon: <PrinterIcon size={24} />,
          onClick: handlePrint,
        },
      ]
      : []),
    ...(!isActiveDocument(activeTab)
      ? [
        {
          description: "Preview",
          icon: <EyeIcon size={24} />,
          onClick: async () => {
            if (isFreeLimit) {
              setShowBuyCreditsModal(true);
              return;
            }
            await handleView(liveWebsiteUrl);
          },
        },
      ]
      : []),
    ...(activeTab !== "Card Maker" && !isActiveDocument(activeTab)
      ? [
        {
          description: "Share",
          customRender: (
            <Button
              variant="ghost"
              className="h-fit md:px-5 px-3 py-2 ml-[auto] dark:bg-white bg-black text-xs dark:text-black text-white dark:hover:text-white dark:hover:bg-black hover:text-foreground flex items-center gap-1 rounded-[50px] ml-3"
              onClick={() => {
                dispatch(setActiveNavItem("z-[46]"));
                setShareOpen(true);
                // console.log("Share clicked");
              }}
            >
              <ShareIcon size={20} />
              Share
            </Button>
          ),
        },
      ]
      : []),
 
  ];

  return (
    <>
      {/* 📱 Mobile-only actions */}
      <div className="flex flex-row lg:hidden">
        {/* <div className="flex flex-row items-center lg:hidden flex-nowrap overflow-x-auto"> */}
        {activeTab == "Page Builder" &&
          <button
            id="codeModeBtn"
            onClick={() => {
              if (isFreeLimit) {
                setShowBuyCreditsModal(true);
                return;
              }
              dispatch(setIsCodeView(!isCodeView))
            }}
            disabled={isLoading || artifact.status === "streaming"}
            className={cn(
              "text-[14px] rounded-md px-1 py-1 transition",
              isLoading || artifact.status === "streaming"
                ? "opacity-50 pointer-events-none cursor-not-allowed"
                : "cursor-pointer underline text-dark dark:text-white hover:bg-accent dark:hover:bg-muted"
            )}
          // className="cursor-pointer text-[14px] text-dark dark:text-white underline transition dark:hover:bg-muted hover:bg-accent rounded-md px-1 py-1"
          >
            {isCodeView ? 'UI Mode' : 'Editor'}
          </button>
        }
        {extraActions.map((action) =>
          action.customRender ? (
            <div
              key={action.description}
              style={{
                pointerEvents:
                  isLoading || artifact.status === "streaming"
                    ? "none"
                    : "auto",
                opacity: isLoading || artifact.status === "streaming" ? 0.5 : 1,
              }}
            >
              {action.customRender}
            </div>
          ) : (
            <Tooltip key={action.description}>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  // className="w-full h-fit p-2 ml-2 border bg-transparent dark:text-white text-black dark:bg-transparent"
                  className={`w-full h-fit p-2 border bg-transparent dark:text-white text-black dark:bg-transparent ${activeTab === "Card Maker" ? "ml-2" : ""
                    }`}
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      await action.onClick(actionContext);
                    } catch (error) {
                      toast.error("Failed to execute action", {
                        style: { color: "red" },
                        position: "top-center",
                      });
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading || artifact.status === "streaming"}
                >
                  {action.icon}
                </Button>
              </TooltipTrigger>
            </Tooltip>
          )
        )}
      </div>

      {/* Credits and Buy Credits button */}
      <div className="flex items-center justify-center w-full lg:w-auto lg:mt-0 mt-2 gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-[14px] text-dark dark:text-white cursor-default">
              Remaining Credits: {user_ai_limit ?? 0}
            </span>
          </TooltipTrigger>
          <TooltipContent align="center">
            <p className="text-[12px] max-w-[260px]">
              <span className="text-accent">⚡</span> Each prompt costs{" "}
              <b>1 credit</b>. You have{" "}
              <span className="text-emerald-400 dark:text-emerald-300 font-semibold">
                {user_ai_limit ?? 0} credits
              </span>{" "}
              remaining.
            </p>
          </TooltipContent>
        </Tooltip>

        <Button
          variant="ghost"
          onClick={() => {
            dispatch(setActiveNavItem("z-[46]"));
            setShowBuyCreditsModal(true);
          }}
          className="lg:hidden h-fit md:px-5 px-3 py-2 ml-[auto] dark:bg-white bg-black text-xs dark:text-black text-white dark:hover:text-white dark:hover:bg-black hover:text-foreground flex items-center gap-1 rounded-[50px] ml-3"
        >
          <Zap />
          Buy Credits
        </Button>

        <SidebarUserNav user={userIdParent} className="group w-auto ms-4 block md:hidden" />

        {/* ✅ Desktop-only actions */}
        <div className="hidden lg:flex items-center gap-1">
          {activeTab == "Page Builder" &&
            <button
              id="codeModeBtn"
              onClick={() => {
                if (isFreeLimit) {
                  setShowBuyCreditsModal(true);
                  return;
                }
                dispatch(setIsCodeView(!isCodeView))
              }}
                disabled={isLoading || artifact.status === "streaming"}
              className={cn(
                "text-[14px] rounded-md px-2 py-1 transition",
                isLoading || artifact.status === "streaming"
                  ? "opacity-50 pointer-events-none cursor-not-allowed"
                  : "cursor-pointer underline text-dark dark:text-white hover:bg-accent dark:hover:bg-muted"
              )}
              // className="cursor-pointer text-[14px] text-dark dark:text-white underline transition dark:hover:bg-muted hover:bg-accent rounded-md px-2 py-1"
            >
              {isCodeView ? 'UI Mode' : 'Editor'}
            </button>
          }
          {extraActions.map((action) =>
            action.customRender ? (
              <div
                key={action.description}
                style={{
                  pointerEvents:
                    isLoading || artifact.status === "streaming"
                      ? "none"
                      : "auto",
                  opacity:
                    isLoading || artifact.status === "streaming" ? 0.5 : 1,
                }}
              >
                {action.customRender}
              </div>
            ) : (
              <Tooltip key={action.description}>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    // className="h-fit p-0 border-0 ml-[12px] bg-[transparent] dark:text-white text-black dark:bg-transparent"
                    className={`h-fit p-0 border-0 bg-[transparent] dark:text-white text-black dark:bg-transparent ${activeTab === "Card Maker" ? "ml-[12px]" : ""
                      }`}
                    onClick={async () => {
                      setIsLoading(true);
                      try {
                        await action.onClick(actionContext);
                      } catch (error) {
                        toast.error("Failed to execute action", {
                          style: { color: "red" },
                          position: "top-center",
                        });
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading || artifact.status === "streaming"}
                  >
                    {action.icon}
                  </Button>
                </TooltipTrigger>
              </Tooltip>
            )
          )}
        </div>
      </div>
    </>
  );
}

export const ArtifactActions = memo(PureArtifactActions, (prevProps, nextProps) => {
  if (prevProps.artifact.status !== nextProps.artifact.status) return false;
  if (prevProps.currentVersionIndex !== nextProps.currentVersionIndex) return false;
  if (prevProps.isCurrentVersion !== nextProps.isCurrentVersion) return false;
  if (prevProps.artifact.content !== nextProps.artifact.content) return false;

  return true;
});
